import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, Link } from "react-router-dom";
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import AdvancedSearch from '../../components/AdvancedSearch/AdvancedSearch';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import axios from 'axios';
import { getOverview } from '../../ApiFunctions.js';
import { Activity, PocketKnife, GitCompareArrows } from 'lucide-react';
import Swal from 'sweetalert2'
import {
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
} from '@chakra-ui/react';

const HomePage = () => {
  // ... (Hover states remain the same)
  const [isDnaHovered, setIsDnaHovered] = useState(false);
  const [isProteinHovered, setIsProteinHovered] = useState(false);
  const [isResidueHovered, setIsResidueHovered] = useState(false);
  const [isResidueGeneHovered, setIsResidueGeneHovered] = useState(false);

  const handleDnaMouseEnter = () => setIsDnaHovered(true);
  const handleDnaMouseLeave = () => setIsDnaHovered(false);
  const handleProteinMouseEnter = () => setIsProteinHovered(true);
  const handleProteinMouseLeave = () => setIsProteinHovered(false);
  const handleResidueMouseEnter = () => setIsResidueHovered(true);
  const handleResidueMouseLeave = () => setIsResidueHovered(false);
  const handleResidueGeneMouseEnter = () => setIsResidueGeneHovered(true);
  const handleResidueGeneMouseLeave = () => setIsResidueGeneHovered(false);

  const navigate = useNavigate();
  const initialValues = { target: "" };
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  // --- LCP FIX: Async Data Loading ---
  const [genesData, setGenesData] = useState([]);
  const [proteinsData, setProteinsData] = useState([]);

  useEffect(() => {
    // This moves the heavy JSON out of the main bundle.
    // The page renders first, then these load in the background.
    const loadData = async () => {
        try {
            const [genesModule, proteinsModule] = await Promise.all([
                import('../../genes.json'),
                import('../../proteins.json')
            ]);
            setGenesData(genesModule.default || genesModule);
            setProteinsData(proteinsModule.default || proteinsModule);
        } catch (err) {
            console.error("Failed to load validation data", err);
        }
    };
    loadData();
  }, []);
  // -------------------------------------

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value.toUpperCase().trim() });
  };

  const onClickHandler = () => setFormValues({ target: "NTS" });
  const onClickHandlerProtein = () => setFormValues({ target: "P30990" });
  const onClickHandlerResidue = () => setFormValues({ target: "P30990_151" });
  const onClickHandlerResidueGene = () => setFormValues({ target: "NTS_151" });

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {}
  }, [formErrors, formValues, isSubmit]);

  const validateForm = (values) => {
    const errors = {};
    const regex = /^[^!@#$%^&*()+{}[\]:;<>,.?°~\\/]+$/;

    if (values.target.trim() === "") {
      errors.target = "Search term is required!";
    } else if (!regex.test(values.target)) {
      errors.target = 'This is not a valid format!';
    }
    return errors;
  };

  // Validation functions remain the same, but now rely on state variables
  function isValidUniProtAccession(input) {
    // Safety check if data hasn't loaded yet
    if (!proteinsData.length) return false;
    return proteinsData.some(protein => protein.PROTEIN_AN === input.trim());
  }

  function isValidResidue(input) {
    if (!proteinsData.length) return false;
    const regex = /^[A-Z0-9]{6,10}(_[0-9]+)$/;
    const found = proteinsData.some(protein => protein.PROTEIN_AN === input.split("_")[0].trim());
    return regex.test(input.trim()) && found;
  }

  function isValidGeneSymbol(input) {
    if (!genesData.length) return false;
    return genesData.some(gene => gene.GENE_SYMBOL === input.trim());
  }

  function isValidGeneResidue(input) {
    if (!genesData.length) return false;
    const regex = /^[A-Z0-9]{2,10}(_[0-9]+)$/;
    const found = genesData.some(gene => gene.GENE_SYMBOL === input.split("_")[0].trim());
    return regex.test(input.trim()) && found;
  }

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Check if data is loaded before allowing submit to prevent errors
    if (genesData.length === 0 || proteinsData.length === 0) {
        Swal.fire({ icon: "info", text: "Initializing database... please wait a moment." });
        return;
    }

    const error = validateForm(formValues);
    setFormErrors(error);
    if (!error.target) {
      setIsLoading(true);
      sendExploreData();
    }
    setIsSubmit(true);
  };

  async function sendExploreData() {

       try {

        const query = {
              query: `
                query getResiduesByGene {
                  residuesByGeneSymbol(geneSymbol: "${formValues.target.toUpperCase()}") {
                    anPosition
                    unRefProtRe
                    afRes
                    unAnnNatVariants
                    unMoleculeProcessing
                    unLocation
                    unMotives
                    unDomains
                    pfamDomainPosHmm
                    unLigandsBindingSite
                    unAnnPropActiveSite
                    phosPtms
                    unGlycosylations
                    unLipidations
                    unModifiedRes
                    unDisulfideBongBridges
                    unSecondaryStructure
                    afPredictedSecondaryStructure
                    afConfidenceValue
                    unCrossLinks
                    inBindingRegion
                    interproDomain
                    unMotivesEvidence
                    unDomainsEvidence
                    unLocationEvidence
                    unMoleculeProcessingEvidence
                    unAnnNatVariantsEvidence
                    unCrossLinksEvidence
                    unLipidationsEvidence
                    unGlycosylationsEvidence
                    unDisulfideBongBridgesEvidence
                    unSecondaryStructureEvidence
                    unAnnPropActiveSiteEvidence
                    unLigandsBindingSiteEvidence
                    unModifiedResEvidence
                    summary
                    aamissense {
                        amClass
                        amMean
                        amMax
                        amMin
                        amPathogenicFrac
                    }
                  }
                }
              `
            };

        const query_protein = {
              query: `
                query getResiduesByProtein {
                  residues(proteinAn: "${formValues.target.toUpperCase()}") {
                    anPosition
                    unRefProtRe
                    afRes
                    unAnnNatVariants
                    unMoleculeProcessing
                    unLocation
                    unMotives
                    unDomains
                    pfamDomainPosHmm
                    unLigandsBindingSite
                    unAnnPropActiveSite
                    phosPtms
                    unGlycosylations
                    unLipidations
                    unModifiedRes
                    unDisulfideBongBridges
                    unSecondaryStructure
                    afPredictedSecondaryStructure
                    afConfidenceValue
                    unCrossLinks
                    inBindingRegion
                    interproDomain
                    unMotivesEvidence
                    unDomainsEvidence
                    unLocationEvidence
                    unMoleculeProcessingEvidence
                    unAnnNatVariantsEvidence
                    unCrossLinksEvidence
                    unLipidationsEvidence
                    unGlycosylationsEvidence
                    unDisulfideBongBridgesEvidence
                    unSecondaryStructureEvidence
                    unAnnPropActiveSiteEvidence
                    unLigandsBindingSiteEvidence
                    unModifiedResEvidence
                    summary
                    aamissense {
                        amClass
                        amMean
                        amMax
                        amMin
                        amPathogenicFrac
                    }
                  }
                }
              `
            };

        const query_residue = {
              query: `
                query getResidueByPosition {
                  residue(anPosition: "${formValues.target.toUpperCase()}") {
                    anPosition
                    unRefProtRe
                    afRes
                    unAnnNatVariants
                    unMoleculeProcessing
                    unLocation
                    unMotives
                    unDomains
                    pfamDomainPosHmm
                    unLigandsBindingSite
                    unAnnPropActiveSite
                    phosPtms
                    unGlycosylations
                    unLipidations
                    unModifiedRes
                    unDisulfideBongBridges
                    unSecondaryStructure
                    afPredictedSecondaryStructure
                    afConfidenceValue
                    unCrossLinks
                    inBindingRegion
                    interproDomain
                    unMotivesEvidence
                    unDomainsEvidence
                    unLocationEvidence
                    unMoleculeProcessingEvidence
                    unAnnNatVariantsEvidence
                    unCrossLinksEvidence
                    unLipidationsEvidence
                    unGlycosylationsEvidence
                    unDisulfideBongBridgesEvidence
                    unSecondaryStructureEvidence
                    unAnnPropActiveSiteEvidence
                    unLigandsBindingSiteEvidence
                    unModifiedResEvidence
                    summary
                    aamissense {
                        amClass
                        amMean
                        amMax
                        amMin
                        amPathogenicFrac
                    }
                  }
                }
              `
            };

        const query_gene_residue = {
              query: `
                query getResidueByGeneAndPosition {
                  residueByGeneSymbolAndPosition(genePosition: "${formValues.target.toUpperCase()}") {
                    anPosition
                    unRefProtRe
                    afRes
                    unAnnNatVariants
                    unMoleculeProcessing
                    unLocation
                    unMotives
                    unDomains
                    pfamDomainPosHmm
                    unLigandsBindingSite
                    unAnnPropActiveSite
                    phosPtms
                    unGlycosylations
                    unLipidations
                    unModifiedRes
                    unDisulfideBongBridges
                    unSecondaryStructure
                    afPredictedSecondaryStructure
                    afConfidenceValue
                    unCrossLinks
                    inBindingRegion
                    interproDomain
                    unMotivesEvidence
                    unDomainsEvidence
                    unLocationEvidence
                    unMoleculeProcessingEvidence
                    unAnnNatVariantsEvidence
                    unCrossLinksEvidence
                    unLipidationsEvidence
                    unGlycosylationsEvidence
                    unDisulfideBongBridgesEvidence
                    unSecondaryStructureEvidence
                    unAnnPropActiveSiteEvidence
                    unLigandsBindingSiteEvidence
                    unModifiedResEvidence
                    summary
                    aamissense {
                        amClass
                        amMean
                        amMax
                        amMin
                        amPathogenicFrac
                    }
                  }
                }
              `
            };

        let navigateData = null;
        let overviewData = null;
        let type = "";
        const apiUrl = 'https://api.atlantis.bioinfolab.sns.it/graphql';

        if (isValidUniProtAccession(formValues.target) && !isValidResidue(formValues.target)){
            //navigateData = await axios.post(apiUrl, query_protein);
            [navigateData, overviewData] = await Promise.all([
                axios.post(apiUrl, query_protein),
                getOverview(formValues.target)
            ]);
           type = "protein";
        } else if (isValidResidue(formValues.target)){
          navigateData = await axios.post(apiUrl, query_residue);
          type = "residue";
        } else if (isValidGeneSymbol(formValues.target)) {
          //navigateData = await axios.post(apiUrl, query);
          [navigateData, overviewData] = await Promise.all([
                axios.post(apiUrl, query),
                getOverview(formValues.target)
            ]);
          type = "gene";
        } else if (isValidGeneResidue(formValues.target)) {
          navigateData = await axios.post(apiUrl, query_gene_residue);
          type = "gene_residue";
        }

        if (!navigateData) {
                setIsLoading(false);
                Swal.fire({
                    icon: "info",
                    text: "No results found! Please change your search criteria and try again."
            });
            return;
        }else if (navigateData.data.errors){
               setIsLoading(false);
                Swal.fire({
                    icon: "error",
                    text: "Oops... \n An error has occurred!"
                });
                 return;
        }

      const handleNavigation = (hasData, path, data, overviewData) => {
          setIsLoading(false);
          if (hasData) {

             // Append the form values as query parameters to the URL
              const url = new URL(window.location.href);
              url.searchParams.set('target', formValues.target);
              url.searchParams.set('type', type);

              navigate(path + url.search, { state: { 'target': formValues.target, 'type': type, 'data': data, 'overview': overviewData } });
          } else {
            Swal.fire({
              icon: "info",
              text: "No results found! Please change your search criteria and try again."
            });
          }
        }

        const checkDataAndNavigate = () => {
          // Check for no errors and data is present
          if (!navigateData.data.errors && navigateData.data.data) {
            switch (type) {
              case "gene":
                handleNavigation(navigateData.data.data.residuesByGeneSymbol.length > 0, '/explore/', navigateData.data.data, overviewData);
                break;
              case "residue":
                handleNavigation(!!navigateData.data.data.residue, '/explore/', navigateData.data.data, overviewData);
                break;
              case "protein":
                handleNavigation(navigateData.data.data.residues.length > 0, '/explore/', navigateData.data.data, overviewData);
                break;
              case "gene_residue":
                handleNavigation(navigateData.data.data.residueByGeneSymbolAndPosition, '/explore/', navigateData.data.data, overviewData);
                break;
              default:
                setIsLoading(false);
                Swal.fire({
                  icon: "info",
                  text: "Invalid search type! Please check your criteria and try again."
                });
            }
          } else {
            // Handle case where there are errors or no data
            setIsLoading(false);
            Swal.fire({
              icon: "info",
              text: "No results found! Please change your search criteria and try again."
            });
          }
        }

       checkDataAndNavigate();

     } catch (error) {

         setIsLoading(false);
         //setError('An error occurred: ' + error.message);

          Swal.fire({
              icon: "error",
              text: error.message
          });
       }
  }

  return (
    <>
      <Helmet>
        <title>Atlantis | Home</title>
        {/* Preload the LCP image to boost priority further */}
        <link rel="preload" as="image" href="/assets/images/logo.webp" />
      </Helmet>
      <Header />
      <section className="home">
        <div className="container min-vh-100 d-flex justify-content-center align-items-center">
          <div className="row align-items-center col-12 col-lg-8">
            <div className="col-md-6 d-flex justify-content-center align-items-center">
              {/* CLS FIX: Added aspect-ratio to style and removed height: auto */}
              <img
                 src="/assets/images/logo.webp"
                 alt="Atlantis Database"
                 width="280"
                 height="110"
                 fetchPriority="high"
                 loading="eager"
                 className="img-fluid"
                 style={{maxWidth: '100%', aspectRatio: '280/110', objectFit: 'contain'}}
              />
            </div>
            <div className="col-md-6 mb-5">
              <p>&nbsp;</p>
              <h1 className="display-6 fw-bold lh-sm">An integrative daTabase for human proteome structuraL And FuNcTIonal Sites</h1>
            </div>
            {/* ... (Form code remains the same) ... */}
            <div className="col-md-12 mb-5">
               {/* ... Form Input ... */}
               <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                 <div className="p-inputgroup flex-1 mb-1">
                     <InputText className="p-input text-lg" name="target" value={formValues.target} onChange={onChangeHandler} style={{ width: '100%' }} placeholder="Search by gene/protein/residue position" />
                      <Button icon="pi pi-filter" type="button" severity="success" onClick={() => setVisible(true)} className="btn-form p-button-rounded  search-button"/>
                      {loading ? (
                          <Button className="btn-form p-button-rounded p-button-secondary search-button" icon="pi pi-spin pi-spinner" />
                      ) : (
                         <Button type="submit" className="btn-form p-button-rounded p-button-secondary search-button" icon="pi pi-search" />
                      )}
                </div>
              </form>

              <div className="row d-flex flex-wrap justify-content-center align-items-center mt-3 mb-4">
                {/* CLS FIX: Ensure buttons don't shift by enforcing dimensions on the icons */}
                <div className="col">
                  <div className="btn-group" role="group">
                    <Button className="btn-example mb-2" onMouseEnter={handleDnaMouseEnter} onMouseLeave={handleDnaMouseLeave} onClick={onClickHandler} tooltip="Search by gene symbol">
                      NTS&nbsp;
                      <img
                        alt="logo"
                        src={isDnaHovered ? "/assets/images/dna.gif" : "/assets/images/dna.webp"}
                        className="img-fluid btn-img"
                        width="30" height="30" // Set explicit small dimensions based on your design
                        style={{aspectRatio: '1/1'}}
                      />
                    </Button>
                  </div>
                </div>
                <div className="col">
                  <div className="btn-group" role="group">
                    <Button className="btn-example mb-2" onMouseEnter={handleProteinMouseEnter} onMouseLeave={handleProteinMouseLeave} onClick={onClickHandlerProtein} tooltip="Search by UniProt accession number">
                      P30990&nbsp;
                      <img
                        alt="Protein"
                        src={isProteinHovered ? "/assets/images/protein.gif" : "/assets/images/protein.webp"}
                        className="img-fluid btn-img"
                        width="30" height="30"
                        style={{aspectRatio: '1/1'}}
                      />
                    </Button>
                  </div>
                </div>
                <div className="col">
                  <div className="btn-group" role="group">
                    <Button className="btn-example mb-2" onMouseEnter={handleResidueMouseEnter} onMouseLeave={handleResidueMouseLeave} onClick={onClickHandlerResidue} tooltip="Search by residue given a UniProt accession number and its protein sequence position">
                      P30990_151&nbsp;
                      <img
                        alt="Residue"
                        src={isResidueHovered ? "/assets/images/chemist.gif" : "/assets/images/chemist.webp"}
                        className="img-fluid btn-img"
                        width="30" height="30"
                        style={{aspectRatio: '1/1'}}
                      />
                    </Button>
                  </div>
                </div>
                <div className="col">
                  <div className="btn-group" role="group">
                    <Button className="btn-example mb-2" onMouseEnter={handleResidueGeneMouseEnter} onMouseLeave={handleResidueGeneMouseLeave} onClick={onClickHandlerResidueGene} tooltip="Search by residue given a gene symbol and its protein sequence position">
                      NTS_151&nbsp;
                      <img
                        alt="Residue Gene"
                        src={isResidueGeneHovered ? "/assets/images/chemist.gif" : "/assets/images/chemist.webp"}
                        className="img-fluid btn-img"
                        width="30" height="30"
                        style={{aspectRatio: '1/1'}}
                      />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="row d-flex justify-content-center align-items-center">
                <StatGroup>
                  {/* If Chakra UI stats cause shifts, add minWidth to containers, but standard text usually ok if fonts preload */}
                  <Stat>
                    <StatNumber className="display-5">11M</StatNumber>
                    <StatLabel>ANNOTATED RESIDUES</StatLabel>
                    <StatHelpText>across 20k human proteins</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatNumber className="display-5">123M</StatNumber>
                    <StatLabel>INTRA-PROTEIN CONTACTS</StatLabel>
                    <StatHelpText>62k PDB · 38k AlphaFold structures</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatNumber className="display-5">19M</StatNumber>
                    <StatLabel>INTER-PROTEIN CONTACTS</StatLabel>
                    <StatHelpText>24k PDB ·15k AlphaFold structures</StatHelpText>
                  </Stat>
                </StatGroup>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Explore More Features Section */}
      <section className="explore-features" style={{
        backgroundColor: '#f8f9fa',
        paddingTop: '4rem',
        paddingBottom: '4rem'
      }}>
        <div className="container">
          <div className="row justify-content-center mb-5">
            <div className="col-12">
              <h2 className="display-6 fw-bold text-center mb-3">Explore More Features</h2>
              <hr style={{ opacity: 0.2, maxWidth: '200px', margin: '0 auto' }} />
            </div>
          </div>

          <div className="row justify-content-center align-items-stretch g-4">
            {/* Module 1: Contacts Calculation */}
            <div className="col-lg-4 col-md-10">
              <Link to="/calculation/" style={{ textDecoration: 'none' }}>
                <div className="module-card" style={{
                  padding: '2rem',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  backgroundColor: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <PocketKnife className="me-3" size={40} />
                  </div>
                  <h3 style={{
                    marginBottom: '0.75rem',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: '#212529'
                  }}>Contacts Calculation</h3>
                  <p style={{
                    marginBottom: '1rem',
                    color: '#999',
                    lineHeight: '1.4',
                    fontSize: '0.85rem',
                    fontStyle: 'italic'
                  }}>Upload & Analyze</p>
                  <p style={{
                    marginBottom: 'auto',
                    color: '#666',
                    lineHeight: '1.6',
                    fontSize: '0.95rem'
                  }}>Process custom 3D structures (mmCIF, PDB IDs, or AlphaFold models) to identify intra- and inter-protein residue contacts with automated functional annotation mapping.</p>
                  <ul style={{
                    marginTop: '1rem',
                    marginBottom: '1.5rem',
                    paddingLeft: '1.25rem',
                    color: '#666',
                    fontSize: '0.9rem',
                    lineHeight: '1.5'
                  }}>
                    <li>Shortest path analysis between residues</li>
                    <li>Structural hub identification</li>
                    <li>Contacts mapping on structures</li>
                  </ul>
                  <div style={{ marginTop: 'auto' }}>
                    <span className="fw-medium" style={{ color: 'var(--primary-color, #007bff)' }}>
                      Upload Structure →
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Module 2: Pathfinder */}
            <div className="col-lg-4 col-md-10">
              <Link to="/pathfinder/" style={{ textDecoration: 'none' }}>
                <div className="module-card" style={{
                  padding: '2rem',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  backgroundColor: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <Activity className="me-3" size={40} />
                  </div>
                  <h3 style={{
                    marginBottom: '0.75rem',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: '#212529'
                  }}>Pathfinder</h3>
                  <p style={{
                    marginBottom: '1rem',
                    color: '#999',
                    lineHeight: '1.4',
                    fontSize: '0.85rem',
                    fontStyle: 'italic'
                  }}>Graph-based Analysis</p>
                  <p style={{
                    marginBottom: 'auto',
                    color: '#666',
                    lineHeight: '1.6',
                    fontSize: '0.95rem'
                  }}>Explore topological and functional analysis on protein structures using graph-based algorithms to uncover allosteric pathways and structural communication.</p>
                  <ul style={{
                    marginTop: '1rem',
                    marginBottom: '1.5rem',
                    paddingLeft: '1.25rem',
                    color: '#666',
                    fontSize: '0.9rem',
                    lineHeight: '1.5'
                  }}>
                    <li>Physical pathways & centrality analysis</li>
                    <li>PTM crosstalk & variant disruption</li>
                    <li>Family-wide evolutionary hotspots</li>
                  </ul>
                  <div style={{ marginTop: 'auto' }}>
                    <span className="fw-medium" style={{ color: 'var(--primary-color, #007bff)' }}>
                      Start Analysis →
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Module 3: Cross-species Projection */}
            <div className="col-lg-4 col-md-10">
              <Link to="/projection/" style={{ textDecoration: 'none' }}>
                <div className="module-card" style={{
                  padding: '2rem',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  backgroundColor: '#ffffff',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <span style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '12px',
                    backgroundColor: '#17507E',
                    color: '#ffffff',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase'
                  }}>New</span>
                  <div style={{ marginBottom: '1rem' }}>
                    <GitCompareArrows className="me-3" size={40} />
                  </div>
                  <h3 style={{
                    marginBottom: '0.75rem',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: '#212529'
                  }}>Cross-species Projection</h3>
                  <p style={{
                    marginBottom: '1rem',
                    color: '#999',
                    lineHeight: '1.4',
                    fontSize: '0.85rem',
                    fontStyle: 'italic'
                  }}>Homology-based Transfer</p>
                  <p style={{
                    marginBottom: 'auto',
                    color: '#666',
                    lineHeight: '1.6',
                    fontSize: '0.95rem'
                  }}>Align a protein from any species to its human homologue and project the annotations that survive that alignment, each labelled with how far it can be trusted.</p>
                  <ul style={{
                    marginTop: '1rem',
                    marginBottom: '1.5rem',
                    paddingLeft: '1.25rem',
                    color: '#666',
                    fontSize: '0.9rem',
                    lineHeight: '1.5'
                  }}>
                    <li>Sequence and structure search, side by side</li>
                    <li>Residue-level mapping to human positions</li>
                    <li>Contacts computed on the query structure</li>
                  </ul>
                  <div style={{ marginTop: 'auto' }}>
                    <span className="fw-medium" style={{ color: 'var(--primary-color, #007bff)' }}>
                      Project Annotations →
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Info Section */}
      <section className="footer-info" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12">
              <p className="mb-2 fs--1 fst-italic text-center">
                This website is free and open to all users. No login is required, and it does not use cookies
              </p>
              <p className="fs--1 fst-italic text-center">
                This work is licensed under CC BY 4.0 &nbsp;
                <a
                  href="https://creativecommons.org/licenses/by/4.0/"
                  target="_blank"
                  rel="license noopener noreferrer"
                >
                  <img
                    style={{ height: '22px', marginLeft: '3px', verticalAlign: 'text-bottom' }}
                    src="https://mirrors.creativecommons.org/presskit/icons/cc.svg?ref=chooser-v1"
                    alt="CC"
                  />
                  <img
                    style={{ height: '22px', marginLeft: '3px', verticalAlign: 'text-bottom' }}
                    src="https://mirrors.creativecommons.org/presskit/icons/by.svg?ref=chooser-v1"
                    alt="BY"
                  />
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
      <div className="advance-search-hidden">
        <AdvancedSearch className="advance-search-hidden" visible={visible} setVisible={setVisible}/>
      </div>
      <Footer />
    </>
  );
};

export default HomePage;